import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom, catchError, of, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { MockChartData } from '../../../mocks/mock-chart-data';
import { NotificationItem, NotificationType } from '../../../models/notification.model';
import { NotificationFilterService } from '../../../shared/services/notification-filter.service';
import { NotificationFilterComponent } from '../../../components/notificationFilter/notification-filter.component';
import { NotificationService } from '../../../shared/services/notification.service';

@Component({
  selector: 'app-charts-main-page',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatIconModule, NotificationFilterComponent],
  templateUrl: './chartMainPage.component.html',
  styleUrls: []
})
export class ChartMainPageComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _environment = inject(EnvironmentService);
  private _notifService = inject(NotificationService);
  private _filterService = inject(NotificationFilterService);
  allCharts: NotificationItem[] = [];
  loading = signal(true);
  error = '';

  private _pushSub?: Subscription;
  private _filterSub?: Subscription;

  async ngOnInit(): Promise<void> {

    this._filterService.reset();
    this._filterSub = this._filterService.currentFilter$.subscribe(() => {
      void this.loadCharts();
    });

    this._pushSub = this._notifService.pushReceived$.subscribe(event => {
      if (event.type === NotificationType.Chart) {
        console.log('[ChartMainPage] Push received, reloading charts...');
        this.loadCharts();
      }
    });
  }

  ngOnDestroy(): void {
    this._pushSub?.unsubscribe();
    this._filterSub?.unsubscribe();
  }
  get filteredCharts(): NotificationItem[] {
    const currentFilter = this._filterService?.getCurrentFilter?.() || 'all';

    switch (currentFilter) {
      case 'unread':
        return this.allCharts?.filter(m => !m.isRead)?.sort((a, b) =>
          new Date(b.created).getTime() - new Date(a.created).getTime()
        ) || this.allCharts || [];
      case 'all':
      default:
        return this.allCharts?.sort((a, b) =>
          new Date(b.created).getTime() - new Date(a.created).getTime()
        ) || this.allCharts || [];
    }
  }

  async loadCharts(): Promise<void> {
    this.loading.set(true);
    this.error = '';

    console.log('[ChartsMainPage] Loading chart notifications...');

    const charts$ = this._environment.isDesignMode
      ? of(this.loadMockCharts())
      : this._notifService.getNotificationsByType(
          NotificationType.Chart,
          this._filterService.getCurrentIsReadFilter()
        );

    await firstValueFrom(
      charts$.pipe(
        catchError(error => {
          console.error('[ChartsMainPage] Error loading charts:', error);
          this.error = 'Error loading charts. Please try again.';
          return of([] as NotificationItem[]);
        })
      )
    ).then(charts => {
      this.allCharts = charts;
      console.log(`[ChartsMainPage] Loaded ${this.allCharts.length} charts`);

      this._filterService.setNotifications(this.allCharts);
    }).finally(() => {
      this.loading.set(false);
    });
  }

  private loadMockCharts(): NotificationItem[] {
    console.log('[ChartsMainPage] Using mock data (design mode)');
    return MockChartData.getChartList();
  }

  onChartClick(chartId: number): void {
    console.log('[ChartsMainPage] Clicked chart ID:', chartId);

    if (!this.isValidChartId(chartId)) {
      console.error('[ChartsMainPage] Invalid chart ID:', chartId);
      return;
    }

    const numericId = Number(chartId);
    console.log('[ChartsMainPage] Navigating to chart detail:', numericId);
    this._router.navigate(['/chart/detail', numericId]);
  }

  private isValidChartId(chartId: number): boolean {
    if (!chartId) {
      return false;
    }

    const numericId = Number(chartId);
    return !isNaN(numericId) && numericId > 0;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';

    return new Date(dateString).toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getPreviewText(body: string, maxLength: number = 100): string {
    return this._notifService.getPreviewText(body, maxLength);
  }

  getReadStatusText(isRead: boolean): string {
    return this._notifService.getReadStatusText(isRead);
  }

  get charts(): NotificationItem[] {
    return this.allCharts;
  }
}
